/* eslint-disable no-restricted-syntax */
/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from "react";
import { t } from "i18next";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PublicIcon from "@mui/icons-material/Public";

import { PUBLIC, VALIDATED, PUBLISHED } from "contants";
import { useDispatch } from "react-redux";
import { display } from "redux-react/reducers/snackBarReducer";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { Share } from "@mui/icons-material";
import { Icon, Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import AccessChip from "../AccessChip";
import DetailContent from "./DetailContent";
import KnowledgeContent from "./KnowledgeContent";

const drawerStyles = readMore => ({
	"& .MuiDrawer-paper": {
		width: { xs: "100%", sm: readMore ? "80%" : "40%" },
		p: 2,
		height: "100%",
		display: "flex",
		flexDirection: "column",
		overflow: "initial",
		overflowY: "initial",
		overflowX: "initial",
		margin: 0,
		boxSizing: "border-box",
		backgroundColor: "#fff",
		borderRadius: 0,
		maxWidth: "100vw"
	}
});

const getStatusColor = value => {
	if (value === "draft") return "warning";
	if (value === "published") return "success";
	return "info";
};

const AssetDetailsDrawer = ({
	open,
	onClose,
	asset,
	setLocalState,
	formattedFolders,
	reloadAssets,
	readMore: readMoreProps = false
}) => {
	const [readMore, setReadMore] = useState(false);

	const toggleReadMore = () => {
		setReadMore(!readMore);
	};

	const dispatch = useDispatch();

	const statusIcons = {
		[PUBLISHED]: <PublicIcon fontSize="small" />,
		[VALIDATED]: <TaskAltIcon fontSize="small" />
	};

	const getFolderPath = (folderId, folders) => {
		const path = [];
		const findFolder = (id, folderList) => {
			for (const folder of folderList) {
				if (folder.id === id) {
					path.unshift(folder);
					if (folder.parentId) {
						findFolder(folder.parentId, folders);
					}
					return;
				}
				if (folder.children && folder.children.length > 0) {
					findFolder(id, folder.children);
				}
			}
		};

		if (folderId) {
			findFolder(folderId, folders);
		}
		return path;
	};

	const copyToClipboard = e => {
		e.stopPropagation();
		navigator.clipboard
			.writeText(asset?.accessUrl)
			.then(() => {
				dispatch(
					display({
						type: "success",
						message: t("ASSETS.TABLE.urlCopied")
					})
				);
			})
			.catch(err => {
				dispatch(
					display({
						type: "error",
						message: t("ASSETS.TABLE.urlCopyFailed")
					})
				);
			});
	};

	useEffect(() => {
		if (!open) {
			setReadMore(false);
		} else {
			setReadMore(asset?.fileType === "theme" ? false : readMoreProps);
		}
	}, [open]);

	return (
		<Drawer anchor="right" open={open} onClose={onClose} sx={drawerStyles(readMore)}>
			{/* Header */}
			<MDBox mb={2}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<MDBox>
						<MuiBreadcrumbs aria-label="breadcrumb">
							<MDTypography
								component="span"
								variant="body2"
								color="dark"
								opacity={0.5}
								sx={{ lineHeight: 0 }}
							>
								<Icon>home</Icon>
							</MDTypography>
							{getFolderPath(asset?.folderId, formattedFolders).map(folder => (
								<MDTypography
									key={folder.id}
									component="span"
									variant="button"
									fontWeight="regular"
									textTransform="capitalize"
									color="dark"
									opacity="0.5"
									sx={{ lineHeight: 0 }}
								>
									{folder.name}
								</MDTypography>
							))}
						</MuiBreadcrumbs>
						<MDTypography variant="h5" color="dark">
							{asset?.name}
						</MDTypography>
					</MDBox>
					<Box
						display="flex"
						alignItems="center"
						sx={{
							gap: 1
						}}
					>
						<Chip
							label={t(`ASSETS.STATUS.${asset?.status}`)}
							size="small"
							color={getStatusColor(asset?.status)}
							variant="outlined"
							icon={statusIcons[asset?.status] || <EditNoteIcon fontSize="small" />}
						/>

						<AccessChip
							access={asset?.access || "private"}
							format={asset?.format}
							_id={asset?._id}
							allowChange={false}
						/>

						<Tooltip title={t("SETTINGS.share")}>
							<IconButton onClick={copyToClipboard} edge="end" disabled={asset?.access !== PUBLIC}>
								<Share />
							</IconButton>
						</Tooltip>
						<Tooltip title={t("SETTINGS.close")}>
							<IconButton onClick={onClose} edge="end">
								<CloseIcon />
							</IconButton>
						</Tooltip>
						<MDBox
							display="flex"
							alignItems="center"
							sx={{
								ml: 1,
								borderRadius: "100px",
								backgroundColor: asset?.fileType === "theme" ? "grey.300" : "info.main",
								color: "white",
								pl: 1,
								pr: 1
							}}
						>
							<Tooltip
								title={
									readMore ? t("ASSETS.TABLE.hideKnowledges") : t("ASSETS.TABLE.showKnowledges")
								}
							>
								<IconButton
									disabled={asset?.fileType === "theme"}
									onClick={toggleReadMore}
									edge="end"
									color="white"
									size="small"
								>
									<Icon>{readMore ? "chevron_right" : "chevron_left"}</Icon>
								</IconButton>
							</Tooltip>
						</MDBox>
					</Box>
				</Box>
				<MDTypography variant="body2" color="text">
					{asset?.description || t("ASSETS.DETAILS.no_description")}
				</MDTypography>
			</MDBox>

			<MDBox sx={{ height: "100%", display: "flex", flexDirection: "row", overflow: "auto" }}>
				<DetailContent asset={asset} setLocalState={setLocalState} />
				{readMore && <KnowledgeContent asset={asset} reloadAssets={reloadAssets} />}
			</MDBox>
		</Drawer>
	);
};

export default AssetDetailsDrawer;
